/**
 * The staff sign-in log: every sign-in, sign-out and refusal for an account that must use an
 * authenticator. The identity server writes the rows; the account API reads them back either as
 * paired sessions (one row per sign-in, with how and when it ended) or as the raw events.
 *
 * Paging is the Account API's own `PagedResult<T>` (`total`, not `totalCount` - the shape used by
 * the admin audit trail), not `security.model.ts`'s.
 */
export interface PagedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export type StaffSignInView = 'sessions' | 'events';

/**
 * How a session ended. `active` is still signed in; `time_limit` is computed server-side from the
 * session length, so a session with no sign-out shows it once that time has passed.
 */
export type StaffSignInEndedBy = 'active' | 'signed_out' | 'admin' | 'time_limit' | 'token_refused';

export type StaffSignInMethod = 'authenticator' | 'recovery' | 'enrolment' | 'password';

export interface StaffSignInSession {
  sessionId: string;
  userId: string;
  userName: string;
  signedInUtc: string;
  endedUtc: string | null;
  endedBy: StaffSignInEndedBy;
  /** The admin who ended it, when `endedBy` is `admin`. */
  endedByActorUserId: string | null;
  method: StaffSignInMethod;
  /** plus-admin · plus-business · plus-desktop · marketplace · other */
  app: string | null;
  ip: string | null;
  countryCode: string | null;
  userAgent: string | null;
}

/** One raw row of the log, every column as the identity server wrote it. */
export interface StaffSignInEvent {
  staffSignInEventId: number;
  occurredAtUtc: string;
  userId: string;
  userName: string | null;
  /** signed_in · signed_out · refused · enrolled · authenticator_reset · session_ended · token_refused */
  eventType: string;
  method: string | null;
  reason: string | null;
  sessionId: string | null;
  clientId: string | null;
  app: string | null;
  ip: string | null;
  countryCode: string | null;
  userAgent: string | null;
  actorUserId: string | null;
  detail: string | null;
}

export interface StaffSignInQuery {
  /** Defaults to `sessions` on the server. */
  view?: StaffSignInView;
  userId?: string | null;
  fromUtc?: string | null;
  toUtc?: string | null;
  /** Sessions view only. */
  endedBy?: StaffSignInEndedBy | '' | null;
  page?: number;
  /** Default 25, at most 200. */
  pageSize?: number;
}

/** Reading labels for the log's codes, in the order they are offered as filters. */
export const STAFF_SIGN_IN_ENDED_BY_LABELS: Record<StaffSignInEndedBy, string> = {
  active: 'Active',
  signed_out: 'Signed out',
  admin: 'Ended by admin',
  time_limit: 'Time limit',
  token_refused: 'Refused by safety net'
};

export const STAFF_SIGN_IN_METHOD_LABELS: Record<string, string> = {
  authenticator: 'Authenticator',
  recovery: 'Recovery code',
  enrolment: 'Set-up',
  password: 'Password',
  pin: 'PIN',
  google: 'Google',
  facebook: 'Facebook',
  admin: 'Admin'
};

export const STAFF_SIGN_IN_EVENT_LABELS: Record<string, string> = {
  signed_in: 'Signed in',
  signed_out: 'Signed out',
  refused: 'Refused',
  enrolled: 'Authenticator set up',
  authenticator_reset: 'Authenticator reset',
  session_ended: 'Session ended',
  token_refused: 'Token refused'
};

export const STAFF_SIGN_IN_REASON_LABELS: Record<string, string> = {
  invalid_credentials: 'Wrong password',
  invalid_code: 'Wrong code',
  locked_out: 'Locked out',
  disabled: 'Account disabled',
  not_allowed_for_staff: 'Not allowed for staff',
  recording_failed: 'Could not be recorded',
  authenticator_reset: 'Authenticator reset',
  account_disabled: 'Account disabled',
  no_mfa: 'No authenticator',
  expired: 'Expired'
};

/** Friendly names for the `App` column, resolved by the identity server from the client id. */
export const STAFF_SIGN_IN_APP_LABELS: Record<string, string> = {
  'plus-admin': 'Plus.Admin',
  'plus-business': '+Business',
  'plus-desktop': '+Business desktop',
  marketplace: 'Marketplace',
  other: 'Other'
};

export function staffSignInEndedByLabel(endedBy?: string | null): string {
  if (!endedBy) return '—';
  return STAFF_SIGN_IN_ENDED_BY_LABELS[endedBy as StaffSignInEndedBy] || endedBy;
}

export function staffSignInMethodLabel(method?: string | null): string {
  if (!method) return '—';
  return STAFF_SIGN_IN_METHOD_LABELS[method] || method;
}

export function staffSignInEventLabel(eventType?: string | null): string {
  if (!eventType) return '—';
  return STAFF_SIGN_IN_EVENT_LABELS[eventType] || eventType;
}

export function staffSignInReasonLabel(reason?: string | null): string {
  if (!reason) return '—';
  return STAFF_SIGN_IN_REASON_LABELS[reason] || reason;
}

export function staffSignInAppLabel(app?: string | null): string {
  if (!app) return '—';
  return STAFF_SIGN_IN_APP_LABELS[app] || app;
}

/**
 * "Chrome, Windows" rather than the whole user-agent string: enough to tell a colleague's laptop
 * from a stranger's phone. Unknown strings come back shortened, not hidden.
 */
export function shortUserAgent(userAgent?: string | null): string {
  const ua = (userAgent || '').trim();
  if (!ua) return '';

  let browser = '';
  if (/\bEdg(e|A|iOS)?\//i.test(ua)) browser = 'Edge';
  else if (/\bOPR\//i.test(ua) || /\bOpera\b/i.test(ua)) browser = 'Opera';
  else if (/\bFirefox\//i.test(ua) || /\bFxiOS\//i.test(ua)) browser = 'Firefox';
  else if (/\bChrome\//i.test(ua) || /\bCriOS\//i.test(ua)) browser = 'Chrome';
  else if (/\bSafari\//i.test(ua)) browser = 'Safari';
  else if (/\bElectron\//i.test(ua)) browser = 'Desktop app';

  let os = '';
  if (/\bWindows\b/i.test(ua)) os = 'Windows';
  else if (/\biPhone\b/i.test(ua)) os = 'iPhone';
  else if (/\biPad\b/i.test(ua)) os = 'iPad';
  else if (/\bMac OS X\b/i.test(ua) || /\bMacintosh\b/i.test(ua)) os = 'macOS';
  else if (/\bAndroid\b/i.test(ua)) os = 'Android';
  else if (/\bLinux\b/i.test(ua)) os = 'Linux';

  const parts = [browser, os].filter(p => !!p);
  if (parts.length) return parts.join(', ');
  return ua.length > 40 ? ua.slice(0, 37) + '…' : ua;
}
